import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "~/common/components/ConfirmationModal";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "~/common/mui";

export interface ConfiguratorHeaderViewProps {
  title: string;
  helpUrl: string | null;
  languageSelector: ReactNode;
  accountNavigation: ReactNode;
  logo: ReactNode;
  openUrl?: string;
  saveUrl?: string;
  onBack: () => void;
  onReset: () => Promise<void> | void;
  onOpen?: () => void;
  onSaveRequested?: () => void;
  onSave?: (name: string) => Promise<void> | void;
  showSave: boolean;
  actionsDisabled?: boolean;
  saveDisabledReason?: string | null;
  savePending?: boolean;
  saveError?: string | null;
}

const HeaderRoot = styled("header")(({ theme }) => ({
  width: "100%",
  minHeight: theme.spacing(9.125),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2.125)}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up("desktop-m")]: {
    paddingInline: theme.spacing(3),
  },
  [theme.breakpoints.up("desktop-l")]: {
    paddingInline: theme.spacing(8),
  },
}));

const TitleGroup = styled(Stack)(({ theme }) => ({
  minWidth: 0,
  flexShrink: 1,
  alignItems: "center",
  gap: theme.spacing(1),
}));

const Actions = styled(Stack)(({ theme }) => ({
  flexShrink: 0,
  alignItems: "center",
  gap: theme.spacing(3),
}));

const ActionButtons = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  gap: theme.spacing(1),
}));

const ActionLink = styled("a")(({ theme }) => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  color: theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const HeaderAction = ({
  href,
  icon,
  label,
  onClick,
  target,
  disabled = false,
}: {
  href?: string;
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  target?: "_blank";
  disabled?: boolean;
}) => {
  const action =
    href && !disabled ? (
      <ActionLink aria-label={label} href={href} target={target} rel={target === "_blank" ? "noreferrer" : undefined}>
        {icon}
      </ActionLink>
    ) : (
      <IconButton aria-label={label} onClick={onClick} disabled={disabled} size="small" sx={{ width: 40, height: 40 }}>
        {icon}
      </IconButton>
    );

  return <Tooltip title={label}>{disabled ? <span>{action}</span> : action}</Tooltip>;
};

const SaveConfigurationDialog = ({
  onCancel,
  onSave,
  pending = false,
  serverError,
}: {
  onCancel: () => void;
  onSave: (name: string) => Promise<void> | void;
  pending?: boolean;
  serverError?: string | null;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [attempted, setAttempted] = useState(false);
  const normalizedName = name.trim();
  const nameError = attempted && (!normalizedName || normalizedName.length > 255);

  const submit = async () => {
    setAttempted(true);
    if (!normalizedName || normalizedName.length > 255 || pending) {
      return;
    }
    try {
      await onSave(normalizedName);
    } catch {
      // The owning mutation renders its server error while this dialog remains open.
    }
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="desktop-s">
      <DialogTitle>{t("page.linear.saveConfig.header", "Save configuration")}</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <TextField
          autoFocus
          fullWidth
          label={t("page.linear.saveConfig.label", "Configuration name")}
          value={name}
          inputProps={{ maxLength: 255 }}
          onChange={(event) => setName(event.target.value)}
          error={nameError}
          helperText={
            nameError
              ? normalizedName.length > 255
                ? t("page.linear.saveConfig.maxLength", "Use no more than 255 characters")
                : t("page.linear.saveConfig.helperLabel", "Enter a configuration name")
              : undefined
          }
        />
        {serverError && (
          <Typography role="alert" color="error" variant="bodyMedium" sx={{ mt: 2 }}>
            {serverError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onCancel} disabled={pending}>
          {t("cancel", "Cancel")}
        </Button>
        <Button onClick={() => void submit()} disabled={pending}>
          {t("generic.save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ConfiguratorHeaderView = ({
  title,
  helpUrl,
  languageSelector,
  accountNavigation,
  logo,
  openUrl,
  saveUrl,
  onBack,
  onReset,
  onOpen,
  onSaveRequested,
  onSave,
  showSave,
  actionsDisabled = false,
  saveDisabledReason,
  savePending = false,
  saveError,
}: ConfiguratorHeaderViewProps) => {
  const { t } = useTranslation();
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const resetLabel = t("page.linear.links.reset", "Reset configuration");
  const openLabel = t("page.linear.links.import", "Open configuration");
  const saveLabel = t("page.linear.links.saveConfig", "Save configuration");
  const helpLabel = t("page.linear.links.help", "Help");

  const confirmReset = async () => {
    await onReset();
    setResetConfirmationOpen(false);
  };

  return (
    <>
      <HeaderRoot>
        <TitleGroup direction="row">
          <HeaderAction label={t("common.back", "Back")} icon={<ArrowBackIcon />} onClick={onBack} />
          <Typography variant="headlineSmall" noWrap>
            {title}
          </Typography>
        </TitleGroup>

        <Actions direction="row">
          <ActionButtons direction="row">
            <HeaderAction
              label={resetLabel}
              icon={<RestartAltIcon />}
              disabled={actionsDisabled}
              onClick={() => setResetConfirmationOpen(true)}
            />
            {showSave && (
              <HeaderAction
                label={openLabel}
                icon={<FolderOpenOutlinedIcon />}
                href={openUrl}
                onClick={onOpen}
                disabled={actionsDisabled}
              />
            )}
            {showSave && (
              <HeaderAction
                label={saveDisabledReason || saveLabel}
                icon={<SaveOutlinedIcon />}
                href={saveUrl}
                disabled={actionsDisabled || Boolean(saveDisabledReason)}
                onClick={onSave ? () => setSaveDialogOpen(true) : onSaveRequested}
              />
            )}
            {helpUrl && (
              <HeaderAction label={helpLabel} icon={<ContactSupportOutlinedIcon />} href={helpUrl} target="_blank" />
            )}
          </ActionButtons>
          {languageSelector}
          {accountNavigation}
          {logo}
        </Actions>
      </HeaderRoot>

      {resetConfirmationOpen && (
        <ConfirmationModal
          label={t("page.linear.resetConfirmation.title", "Reset this configuration?")}
          okLabel={t("page.linear.resetConfirmation.okButton", "Reset")}
          cancelLabel={t("cancel", "Cancel")}
          onCancel={() => setResetConfirmationOpen(false)}
          onOk={() => void confirmReset()}
          slotProps={{ okButton: { disabled: actionsDisabled } }}
        />
      )}
      {saveDialogOpen && onSave && (
        <SaveConfigurationDialog
          onCancel={() => setSaveDialogOpen(false)}
          onSave={onSave}
          pending={savePending}
          serverError={saveError}
        />
      )}
    </>
  );
};
